// @flow
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import useSpeechSynthesis from '../../utility/useSpeechSynthesis'
import { FaVolumeOff, FaVolumeUp } from 'react-icons/fa'
import { LANGUAGE_TO_VOICE_LANGUAGE } from '../../constants/speechConstants'
import { ACCENT_BLUE } from '../../constants/styles'
import { getWordsAt } from '../../utility/functions'
import {
	getCurrentStudentLanguage,
	getCurrentStudentTextToSpeechOptions,
} from '../../store/selectors/sharedSelectors'
import { AVAILABLE_LANGUAGES, TEXT_TO_SPEECH_OPTIONS } from '@mission.io/mission-toolkit/constants'
import { LANGUAGE_TO_TRANSLATION_LANGUAGE, translateString } from '../../utility/stringTranslator'

const rate = 1
const pitch = 1

const TextToSpeechButton = styled.button`
	${({ $position }) =>
		$position === 'start'
			? `margin-right: 0.5rem;`
			: $position === 'end'
			? `margin-left: 0.5rem;`
			: ''}
	vertical-align: middle;
	pointer-events: auto;
`

const TextToSpeechLabel = styled.span`
	color: ${ACCENT_BLUE};
`

/**
 * A function to get the string text of the children component
 * @param {Array<React$Node>} children child component of TextToSpeech
 * @returns {string} child, string text of the children component
 */
const getChildrenText = (children: Array<React$Node>): string =>
	React.Children.map(children, child => {
		if (typeof child !== 'string') {
			return getChildrenText(child?.props?.children)
		}
		return child
	})?.join(' ')

/**
 * A HOC react component to use SpeechSynthesis API
 * It has a string children component used for the SpeechSynthesis API
 * @param {{
 *		children: any, children component wrapped for the TextToSpeech,
 *		onStartSpeech: Function, Callback fired when the browser read some text,
 *  }} props
 * @returns {TextToSpeech} React component
 */
export default function TextToSpeech(props: {
	children: any,
	position?: 'start' | 'end',
	onStartSpeech?: (event: TouchEvent | MouseEvent) => void,
	onEndSpeech?: () => void,
}): React$Node {
	let lastWord = ''
	const onBoundary = event => {
		const words = getWordsAt(event.target.text, event?.charIndex)
		const newWord = words?.word
		if (newWord !== lastWord) {
			setBeforeWord(words?.before)
			setWord(words?.word)
			setAfterWord(words?.after)
			lastWord = newWord
		}
	}

	const onEnd = event => {
		setBeforeWord(translatedText ?? text)
		setWord('')
		setAfterWord('')
		lastWord = ''
		if (props.onEndSpeech) {
			props.onEndSpeech()
		}
	}

	const { speak, voices, cancel, speaking, supported } = useSpeechSynthesis({
		onBoundary,
		onEnd,
	})
	const [word, setWord] = useState('')
	const [afterWord, setAfterWord] = useState('')
	const textToSpeechPreferences = useSelector(getCurrentStudentTextToSpeechOptions)
	const studentLanguage = useSelector(getCurrentStudentLanguage) ?? AVAILABLE_LANGUAGES.ENGLISH
	const text = getChildrenText(props.children)
	const currentText = useRef(text)

	const [translatedText, setTranslatedText] = useState<null | string>(null)
	const [beforeWord, setBeforeWord] = useState(text)
	const isMounted = useRef(true)

	useEffect(
		() => () => {
			isMounted.current = false
		},
		[]
	)
	useEffect(() => {
		setTranslatedText(null)
	}, [text])

	const voice: SpeechSynthesisVoice | null = useMemo(() => {
		const voiceGender: 'MALE' | 'FEMALE' =
			textToSpeechPreferences === TEXT_TO_SPEECH_OPTIONS.FEMALE
				? TEXT_TO_SPEECH_OPTIONS.FEMALE
				: TEXT_TO_SPEECH_OPTIONS.MALE

		const languageData = LANGUAGE_TO_VOICE_LANGUAGE[studentLanguage]
		if (!languageData) {
			return null
		}
		const preferredSpeakerNames = new Set(languageData.voices[voiceGender].preferred ?? [])
		const lastResortSpeakerNames = new Set(languageData.voices[voiceGender].lastResort ?? [])
		const languagePrefix = `${languageData.fallbackPrefix}-`

		const preferredSpeakers = [] // match fully qualified language
		const fallbackSpeakers = [] // match only the language (not the dialect)
		const lastResortSpeakers = [] // match language but should not be used unless there are no other options
		for (const possibleVoice of voices) {
			if (possibleVoice.lang === languageData.prefer) {
				if (preferredSpeakerNames.has(possibleVoice.name)) {
					return possibleVoice
				}

				preferredSpeakers.push(possibleVoice)
			}

			if (possibleVoice.lang.startsWith(languagePrefix)) {
				if (lastResortSpeakerNames.has(possibleVoice.name)) {
					lastResortSpeakers.push(possibleVoice)
					continue
				}
				fallbackSpeakers.push(possibleVoice)
			}
		}

		return preferredSpeakers[0] ?? fallbackSpeakers[0] ?? lastResortSpeakers[0]
	}, [studentLanguage, textToSpeechPreferences, voices])

	if (!voice || !supported) {
		return <span>{props.children}</span>
	}

	const startSpeech = (event: TouchEvent | MouseEvent) => {
		translateString('en', LANGUAGE_TO_TRANSLATION_LANGUAGE[studentLanguage] ?? 'en', text)
			.then(translatedText => {
				if (!isMounted.current || text !== currentText.current) {
					return
				}
				setTranslatedText(translatedText)
				speak({ text: translatedText, voice, rate, pitch })
				if (props.onStartSpeech) {
					props.onStartSpeech(event)
				}
			})
			.catch(() => {
				console.warn(`failed to translate (ENGLISH -> ${studentLanguage}) string "${text}"`)
			})
	}
	const position = props.position ?? 'end'

	const startEndSpeechIcon =
		textToSpeechPreferences !== TEXT_TO_SPEECH_OPTIONS.OFF &&
		text.length > 0 &&
		(speaking ? (
			<TextToSpeechButton
				$position={position}
				onClick={e => {
					e.stopPropagation()
					cancel()
				}}>
				<FaVolumeUp />
			</TextToSpeechButton>
		) : (
			<TextToSpeechButton
				$position={position}
				onClick={e => {
					e.stopPropagation()
					startSpeech(e)
				}}>
				<FaVolumeOff />
			</TextToSpeechButton>
		))

	return (
		<span>
			{position === 'start' && startEndSpeechIcon}
			{speaking ? (
				<span className="notranslate">
					<span>{beforeWord}</span>
					<TextToSpeechLabel>{word}</TextToSpeechLabel>
					<span>{afterWord}</span>
				</span>
			) : (
				<span>{props.children}</span>
			)}
			{position === 'end' && startEndSpeechIcon}
		</span>
	)
}
